import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import { FiDownload, FiCopy, FiRefreshCw, FiLink, FiFileText, FiMail, FiPhone, FiMessageSquare, FiWifi, FiUser, FiSettings, FiImage, FiX, FiSave } from 'react-icons/fi';
import { qrcodesAPI, foldersAPI } from '../services/api';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 20px;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
  padding: 24px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const PageTitle = styled.h1`
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PageDescription = styled.p`
  font-size: 10px;
  color: #6b7280;
  margin: 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: start;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const InputSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  padding: 24px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
  
  @media (max-width: 968px) {
    max-height: none;
    overflow-y: visible;
  }
`;

const PreviewSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: sticky;
  top: 100px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
  
  @media (max-width: 968px) {
    position: relative;
    top: 0;
    max-height: none;
    overflow-y: visible;
    min-height: 400px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TypeSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
`;

const TypeButton = styled.button`
  padding: 12px 16px;
  border: 2px solid ${props => props.active ? 'black' : '#e5e7eb'};
  border-radius: 8px;
  background: ${props => props.active ? 'black' : 'white'};
  color: ${props => props.active ? 'white' : '#6b7280'};
  font-weight: 600;
  font-size: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    border-color: black;
    background: ${props => props.active ? 'black' : '#f9fafb'};
  }
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &::placeholder {
    font-size: 8px;
  }

  &:focus {
    outline: none;
    border-color: black;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;

  &::placeholder {
    font-size: 8px;
  }

  &:focus {
    outline: none;
    border-color: black;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: black;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const GenerateButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: black;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: #374151;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
  }
`;

const QRCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
`;

const QRCodeImage = styled.img`
  max-width: 100%;
  height: auto;
  border: 4px solid #f3f4f6;
  border-radius: 12px;
  padding: 20px;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const QRCodePlaceholder = styled.div`
  width: 300px;
  height: 300px;
  border: 4px dashed #d1d5db;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 16px;
  font-weight: 500;
  background: #f9fafb;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 400px;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 12px 20px;
  border: 2px solid ${props => props.primary ? 'black' : '#e5e7eb'};
  border-radius: 8px;
  background: ${props => props.primary ? 'black' : 'white'};
  color: ${props => props.primary ? 'white' : '#6b7280'};
  font-weight: 600;
  font-size: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    border-color: black;
    background: ${props => props.primary ? '#374151' : '#f9fafb'};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SettingsSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: block;
  padding: 12px 16px;
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  cursor: pointer;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  transition: all 0.2s ease;
  margin-bottom: 12px;

  &:hover {
    border-color: black;
    background: #f3f4f6;
    color: #1f2937;
  }
`;

const LogoPreview = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 12px;
`;

const LogoPreviewImage = styled.img`
  max-width: 100px;
  max-height: 100px;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  padding: 8px;
  background: white;
`;

const RemoveLogoButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #dc2626;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;

  &:hover {
    background: #b91c1c;
    transform: scale(1.1);
  }
`;

const QR_TYPES = {
  URL: { icon: FiLink, label: 'URL', color: '#3b82f6' },
  TEXT: { icon: FiFileText, label: 'Text', color: '#10b981' },
  EMAIL: { icon: FiMail, label: 'Email', color: '#f59e0b' },
  PHONE: { icon: FiPhone, label: 'Phone', color: '#8b5cf6' },
  SMS: { icon: FiMessageSquare, label: 'SMS', color: '#ec4899' },
  WIFI: { icon: FiWifi, label: 'WiFi', color: '#06b6d4' },
  VCARD: { icon: FiUser, label: 'vCard', color: '#ef4444' },
};

function QRCodeGenerator() {
  const [qrType, setQrType] = useState('URL');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedQrCodeId, setSavedQrCodeId] = useState(null);
  
  // QR Code Metadata
  const [qrTitle, setQrTitle] = useState('');
  const [qrDescription, setQrDescription] = useState('');
  
  // URL
  const [url, setUrl] = useState('');
  
  // Text
  const [text, setText] = useState('');
  
  // Email
  const [email, setEmail] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  
  // Phone
  const [phone, setPhone] = useState('');
  
  // SMS
  const [smsPhone, setSmsPhone] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  
  // WiFi
  const [wifiSSID, setWifiSSID] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [wifiSecurity, setWifiSecurity] = useState('WPA');
  const [wifiHidden, setWifiHidden] = useState(false);
  
  // vCard
  const [vcardName, setVcardName] = useState('');
  const [vcardOrg, setVcardOrg] = useState('');
  const [vcardPhone, setVcardPhone] = useState('');
  const [vcardEmail, setVcardEmail] = useState('');
  const [vcardWebsite, setVcardWebsite] = useState('');
  const [vcardAddress, setVcardAddress] = useState('');
  
  // QR Code Settings
  const [qrSize, setQrSize] = useState(300);
  const [qrErrorCorrection, setQrErrorCorrection] = useState('M');
  const [qrColorDark, setQrColorDark] = useState('#000000');
  const [qrColorLight, setQrColorLight] = useState('#FFFFFF');
  const [qrMargin, setQrMargin] = useState(4);
  
  // QR Code Style Options
  const [qrBackgroundColor, setQrBackgroundColor] = useState('#FFFFFF');
  const [qrBorderRadius, setQrBorderRadius] = useState(0);
  const [qrBorderWidth, setQrBorderWidth] = useState(0);
  const [qrBorderColor, setQrBorderColor] = useState('#000000');
  const [qrBackgroundPattern, setQrBackgroundPattern] = useState('none'); // none, gradient, dots, lines
  
  // Logo Settings
  const [logoImage, setLogoImage] = useState(null);
  const [logoSize, setLogoSize] = useState(20); // Percentage of QR code size
  
  // Folder Settings
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [loadingFolders, setLoadingFolders] = useState(false);

  const buildQRContent = () => {
    switch (qrType) {
      case 'URL':
        return url || '';
      
      case 'TEXT':
        return text || '';
      
      case 'EMAIL':
        if (!email) return '';
        let emailContent = `mailto:${email}`;
        const params = [];
        if (emailSubject) params.push(`subject=${encodeURIComponent(emailSubject)}`);
        if (emailBody) params.push(`body=${encodeURIComponent(emailBody)}`);
        if (params.length > 0) emailContent += `?${params.join('&')}`;
        return emailContent;
      
      case 'PHONE':
        return phone ? `tel:${phone}` : '';
      
      case 'SMS':
        if (!smsPhone) return '';
        let smsContent = `sms:${smsPhone}`;
        if (smsMessage) smsContent += `:${encodeURIComponent(smsMessage)}`;
        return smsContent;
      
      case 'WIFI':
        if (!wifiSSID) return '';
        const security = wifiSecurity === 'None' ? 'nopass' : wifiSecurity.toLowerCase();
        const hidden = wifiHidden ? 'H:true' : 'H:false';
        return `WIFI:T:${security};S:${wifiSSID};P:${wifiPassword || ''};${hidden};;`;
      
      case 'VCARD':
        const vcard = [
          'BEGIN:VCARD',
          'VERSION:3.0',
          vcardName ? `FN:${vcardName}` : '',
          vcardOrg ? `ORG:${vcardOrg}` : '',
          vcardPhone ? `TEL:${vcardPhone}` : '',
          vcardEmail ? `EMAIL:${vcardEmail}` : '',
          vcardWebsite ? `URL:${vcardWebsite}` : '',
          vcardAddress ? `ADR:;;${vcardAddress};;;` : '',
          'END:VCARD'
        ].filter(line => line).join('\n');
        return vcard;
      
      default:
        return '';
    }
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoImage(e.target.result);
      setQrCodeUrl(''); // Clear QR code so user knows to regenerate with new logo
      toast.success('Logo uploaded successfully');
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoImage(null);
    setQrCodeUrl(''); // Clear QR code so user knows to regenerate
    toast.success('Logo removed');
  };

  const applyQRCodeStyles = async (qrDataUrl) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = qrSize;
      canvas.height = qrSize;

      const qrImage = new Image();
      qrImage.onload = () => {
        // Helper function to draw rounded rectangle
        const drawRoundedRect = (x, y, width, height, radius) => {
          ctx.beginPath();
          ctx.moveTo(x + radius, y);
          ctx.lineTo(x + width - radius, y);
          ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
          ctx.lineTo(x + width, y + height - radius);
          ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
          ctx.lineTo(x + radius, y + height);
          ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
          ctx.lineTo(x, y + radius);
          ctx.quadraticCurveTo(x, y, x + radius, y);
          ctx.closePath();
        };

        // Draw background
        ctx.fillStyle = qrBackgroundColor;
        if (qrBorderRadius > 0) {
          drawRoundedRect(0, 0, qrSize, qrSize, qrBorderRadius);
          ctx.fill();
        } else {
          ctx.fillRect(0, 0, qrSize, qrSize);
        }

        // Apply background pattern
        if (qrBackgroundPattern === 'gradient') {
          const gradient = ctx.createLinearGradient(0, 0, qrSize, qrSize);
          gradient.addColorStop(0, qrBackgroundColor);
          gradient.addColorStop(1, qrColorLight);
          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, qrSize, qrSize);
        } else if (qrBackgroundPattern === 'dots') {
          ctx.fillStyle = qrColorLight;
          const dotSize = 4;
          const spacing = 12;
          for (let x = 0; x < qrSize; x += spacing) {
            for (let y = 0; y < qrSize; y += spacing) {
              ctx.beginPath();
              ctx.arc(x, y, dotSize / 2, 0, Math.PI * 2);
              ctx.fill();
            }
          }
        } else if (qrBackgroundPattern === 'lines') {
          ctx.strokeStyle = qrColorLight;
          ctx.lineWidth = 1;
          for (let i = 0; i < qrSize; i += 10) {
            ctx.beginPath();
            ctx.moveTo(i, 0);
            ctx.lineTo(i, qrSize);
            ctx.stroke();
          }
        }

        // Draw QR code with clipping for border radius
        if (qrBorderRadius > 0) {
          ctx.save();
          drawRoundedRect(0, 0, qrSize, qrSize, qrBorderRadius);
          ctx.clip();
        }
        
        ctx.drawImage(qrImage, 0, 0, qrSize, qrSize);
        
        if (qrBorderRadius > 0) {
          ctx.restore();
        }

        // Apply border
        if (qrBorderWidth > 0) {
          ctx.strokeStyle = qrBorderColor;
          ctx.lineWidth = qrBorderWidth;
          if (qrBorderRadius > 0) {
            drawRoundedRect(qrBorderWidth / 2, qrBorderWidth / 2, 
                         qrSize - qrBorderWidth, qrSize - qrBorderWidth, qrBorderRadius);
            ctx.stroke();
          } else {
            ctx.strokeRect(qrBorderWidth / 2, qrBorderWidth / 2, 
                          qrSize - qrBorderWidth, qrSize - qrBorderWidth);
          }
        }

        resolve(canvas.toDataURL('image/png'));
      };
      qrImage.onerror = reject;
      qrImage.src = qrDataUrl;
    });
  };

  const generateQRCode = async () => {
    const content = buildQRContent();
    
    if (!content) {
      toast.error('Please fill in the required fields');
      return;
    }

    try {
      setIsGenerating(true);
      
      const options = {
        width: qrSize,
        margin: qrMargin,
        color: {
          dark: qrColorDark,
          light: qrColorLight,
        },
        errorCorrectionLevel: qrErrorCorrection,
        rendererOpts: {
          quality: 1.0,
        },
      };

      // Generate QR code as data URL
      let qrDataUrl = await QRCode.toDataURL(content, options);
      
      // Apply custom styling using canvas
      if (qrBorderRadius > 0 || qrBorderWidth > 0 || qrBackgroundPattern !== 'none' || qrBackgroundColor !== '#FFFFFF') {
        qrDataUrl = await applyQRCodeStyles(qrDataUrl);
      }
      
      // If logo is provided, overlay it on the QR code
      if (logoImage) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = qrSize;
        canvas.height = qrSize;

        // Helper function to draw rounded rectangle
        const drawRoundedRect = (x, y, width, height, radius) => {
          ctx.beginPath();
          ctx.moveTo(x + radius, y);
          ctx.lineTo(x + width - radius, y);
          ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
          ctx.lineTo(x + width, y + height - radius);
          ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
          ctx.lineTo(x + radius, y + height);
          ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
          ctx.lineTo(x, y + radius);
          ctx.quadraticCurveTo(x, y, x + radius, y);
          ctx.closePath();
        };

        // Draw background with styles
        ctx.fillStyle = qrBackgroundColor;
        if (qrBorderRadius > 0) {
          drawRoundedRect(0, 0, qrSize, qrSize, qrBorderRadius);
          ctx.fill();
        } else {
          ctx.fillRect(0, 0, qrSize, qrSize);
        }

        // Apply background pattern
        if (qrBackgroundPattern === 'gradient') {
          const gradient = ctx.createLinearGradient(0, 0, qrSize, qrSize);
          gradient.addColorStop(0, qrBackgroundColor);
          gradient.addColorStop(1, qrColorLight);
          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, qrSize, qrSize);
        } else if (qrBackgroundPattern === 'dots') {
          ctx.fillStyle = qrColorLight;
          const dotSize = 4;
          const spacing = 12;
          for (let x = 0; x < qrSize; x += spacing) {
            for (let y = 0; y < qrSize; y += spacing) {
              ctx.beginPath();
              ctx.arc(x, y, dotSize / 2, 0, Math.PI * 2);
              ctx.fill();
            }
          }
        } else if (qrBackgroundPattern === 'lines') {
          ctx.strokeStyle = qrColorLight;
          ctx.lineWidth = 1;
          for (let i = 0; i < qrSize; i += 10) {
            ctx.beginPath();
            ctx.moveTo(i, 0);
            ctx.lineTo(i, qrSize);
            ctx.stroke();
          }
        }

        // Draw QR code with clipping for border radius
        const qrImage = new Image();
        qrImage.src = qrDataUrl;
        
        await new Promise((resolve, reject) => {
          qrImage.onload = () => {
            if (qrBorderRadius > 0) {
              ctx.save();
              drawRoundedRect(0, 0, qrSize, qrSize, qrBorderRadius);
              ctx.clip();
            }
            
            ctx.drawImage(qrImage, 0, 0, qrSize, qrSize);
            
            if (qrBorderRadius > 0) {
              ctx.restore();
            }
            
            // Draw logo in center
            const logoImg = new Image();
            logoImg.onload = () => {
              // Calculate maximum logo size (percentage of QR code size)
              const maxLogoSize = (qrSize * logoSize) / 100;
              
              // Get original logo dimensions and aspect ratio
              const logoAspectRatio = logoImg.width / logoImg.height;
              
              // Calculate dimensions maintaining aspect ratio
              // Use the larger dimension to determine scaling
              let logoWidth, logoHeight;
              if (logoImg.width >= logoImg.height) {
                // Logo is wider or square - use width as base
                logoWidth = maxLogoSize;
                logoHeight = maxLogoSize / logoAspectRatio;
              } else {
                // Logo is taller - use height as base
                logoHeight = maxLogoSize;
                logoWidth = maxLogoSize * logoAspectRatio;
              }
              
              // Calculate position to center the logo
              const x = (qrSize - logoWidth) / 2;
              const y = (qrSize - logoHeight) / 2;
              
              // Draw white background for logo (to ensure it's visible)
              const padding = 6;
              ctx.fillStyle = qrColorLight;
              ctx.fillRect(
                x - padding,
                y - padding,
                logoWidth + (padding * 2),
                logoHeight + (padding * 2)
              );
              
              // Draw logo with smooth rendering
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';
              ctx.drawImage(logoImg, x, y, logoWidth, logoHeight);
              
              // Apply border
              if (qrBorderWidth > 0) {
                ctx.strokeStyle = qrBorderColor;
                ctx.lineWidth = qrBorderWidth;
                if (qrBorderRadius > 0) {
                  drawRoundedRect(qrBorderWidth / 2, qrBorderWidth / 2, 
                               qrSize - qrBorderWidth, qrSize - qrBorderWidth, qrBorderRadius);
                  ctx.stroke();
                } else {
                  ctx.strokeRect(qrBorderWidth / 2, qrBorderWidth / 2, 
                                qrSize - qrBorderWidth, qrSize - qrBorderWidth);
                }
              }
              
              const finalDataUrl = canvas.toDataURL('image/png');
              setQrCodeUrl(finalDataUrl);
              resolve();
            };
            logoImg.onerror = reject;
            logoImg.src = logoImage;
          };
          qrImage.onerror = reject;
        });
      } else {
        setQrCodeUrl(qrDataUrl);
      }
      
      toast.success('QR code generated successfully!');
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeUrl) {
      toast.error('No QR code to download');
      return;
    }

    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `qrcode-${qrType.toLowerCase()}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code downloaded successfully!');
  };

  const copyQRCode = () => {
    if (!qrCodeUrl) {
      toast.error('No QR code to copy');
      return;
    }

    // Convert data URL to blob
    fetch(qrCodeUrl)
      .then(res => res.blob())
      .then(blob => {
        const item = new ClipboardItem({ 'image/png': blob });
        navigator.clipboard.write([item]).then(() => {
          toast.success('QR code copied to clipboard!');
        }).catch(() => {
          toast.error('Failed to copy QR code');
        });
      })
      .catch(() => {
        toast.error('Failed to copy QR code');
      });
  };

  const generateQRCodeWithRedirect = async (qrCodeId) => {
    // Generate QR code that points directly to backend redirect endpoint
    // This allows dynamic updates - the backend will redirect to the current content
    // Use the same origin as the frontend (in production, frontend and backend are on same domain)
    const redirectUrl = `${window.location.origin}/api/qrcodes/redirect/${qrCodeId}`;
    
    const options = {
      width: qrSize,
      margin: qrMargin,
      color: {
        dark: qrColorDark,
        light: qrColorLight,
      },
      errorCorrectionLevel: qrErrorCorrection,
      rendererOpts: {
        quality: 1.0,
      },
    };

    let qrDataUrl = await QRCode.toDataURL(redirectUrl, options);
    
    // Apply custom styling using canvas
    if (qrBorderRadius > 0 || qrBorderWidth > 0 || qrBackgroundPattern !== 'none' || qrBackgroundColor !== '#FFFFFF') {
      qrDataUrl = await applyQRCodeStyles(qrDataUrl);
    }
    
    // If logo is provided, overlay it on the QR code
    if (logoImage) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = qrSize;
      canvas.height = qrSize;

      // Helper function to draw rounded rectangle
      const drawRoundedRect = (x, y, width, height, radius) => {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
      };

      // Draw background with styles
      ctx.fillStyle = qrBackgroundColor;
      if (qrBorderRadius > 0) {
        drawRoundedRect(0, 0, qrSize, qrSize, qrBorderRadius);
        ctx.fill();
      } else {
        ctx.fillRect(0, 0, qrSize, qrSize);
      }

      // Apply background pattern
      if (qrBackgroundPattern === 'gradient') {
        const gradient = ctx.createLinearGradient(0, 0, qrSize, qrSize);
        gradient.addColorStop(0, qrBackgroundColor);
        gradient.addColorStop(1, qrColorLight);
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, qrSize, qrSize);
      } else if (qrBackgroundPattern === 'dots') {
        ctx.fillStyle = qrColorLight;
        const dotSize = 4;
        const spacing = 12;
        for (let x = 0; x < qrSize; x += spacing) {
          for (let y = 0; y < qrSize; y += spacing) {
            ctx.beginPath();
            ctx.arc(x, y, dotSize / 2, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      } else if (qrBackgroundPattern === 'lines') {
        ctx.strokeStyle = qrColorLight;
        ctx.lineWidth = 1;
        for (let i = 0; i < qrSize; i += 10) {
          ctx.beginPath();
          ctx.moveTo(i, 0);
          ctx.lineTo(i, qrSize);
          ctx.stroke();
        }
      }

      const qrImage = new Image();
      qrImage.src = qrDataUrl;
      
      const finalDataUrl = await new Promise((resolve, reject) => {
        qrImage.onload = () => {
          // Draw QR code with clipping for border radius
          if (qrBorderRadius > 0) {
            ctx.save();
            drawRoundedRect(0, 0, qrSize, qrSize, qrBorderRadius);
            ctx.clip();
          }
          
          ctx.drawImage(qrImage, 0, 0, qrSize, qrSize);
          
          if (qrBorderRadius > 0) {
            ctx.restore();
          }
          
          const logoImg = new Image();
          logoImg.onload = () => {
            const maxLogoSize = (qrSize * logoSize) / 100;
            const logoAspectRatio = logoImg.width / logoImg.height;
            
            let logoWidth, logoHeight;
            if (logoImg.width >= logoImg.height) {
              logoWidth = maxLogoSize;
              logoHeight = maxLogoSize / logoAspectRatio;
            } else {
              logoHeight = maxLogoSize;
              logoWidth = maxLogoSize * logoAspectRatio;
            }
            
            const x = (qrSize - logoWidth) / 2;
            const y = (qrSize - logoHeight) / 2;
            
            const padding = 6;
            ctx.fillStyle = qrColorLight;
            ctx.fillRect(
              x - padding,
              y - padding,
              logoWidth + (padding * 2),
              logoHeight + (padding * 2)
            );
            
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(logoImg, x, y, logoWidth, logoHeight);
            
            // Apply border
            if (qrBorderWidth > 0) {
              ctx.strokeStyle = qrBorderColor;
              ctx.lineWidth = qrBorderWidth;
              if (qrBorderRadius > 0) {
                drawRoundedRect(qrBorderWidth / 2, qrBorderWidth / 2, 
                             qrSize - qrBorderWidth, qrSize - qrBorderWidth, qrBorderRadius);
                ctx.stroke();
              } else {
                ctx.strokeRect(qrBorderWidth / 2, qrBorderWidth / 2, 
                              qrSize - qrBorderWidth, qrSize - qrBorderWidth);
              }
            }
            
            resolve(canvas.toDataURL('image/png'));
          };
          logoImg.onerror = reject;
          logoImg.src = logoImage;
        };
        qrImage.onerror = reject;
      });
      
      return finalDataUrl;
    }
    
    return qrDataUrl;
  };

  const saveQRCode = async () => {
    // In dynamic mode we care about the logical destination (content);
    // the actual QR image will encode a stable redirect URL (/qr/:id).
    if (!qrTitle.trim()) {
      toast.error('Please enter a title for the QR code');
      return;
    }

    const content = buildQRContent();
    if (!content) {
      toast.error('Please fill in the required fields');
      return;
    }

    try {
      setIsSaving(true);

      if (savedQrCodeId) {
        // DYNAMIC QR MODE (existing code):
        // Only update the destination in the database.
        // The QR image already points to /qr/:id, so printed codes stay valid
        // and will now open the updated URL.
        await qrcodesAPI.updateContent(savedQrCodeId, content, qrType);
        toast.success('QR destination updated successfully. The same printed QR will now open the new URL.');
      } else {
        // DYNAMIC QR MODE (new code):
        // 1) Create the logical record with destination content.
        // 2) Generate a QR image that encodes a stable redirect URL (/qr/:id).
        // 3) Update the record with that QR image so all scans go via redirect.
        const qrCodeData = {
          title: qrTitle.trim(),
          description: qrDescription.trim() || null,
          qr_type: qrType,
          content: content,
          // Temporary image value (will be replaced right after create)
          qr_code_image: qrCodeUrl || 'placeholder',
          qr_code_settings: JSON.stringify({
            size: qrSize,
            errorCorrection: qrErrorCorrection,
            colorDark: qrColorDark,
            colorLight: qrColorLight,
            margin: qrMargin
          }),
          logo_image: logoImage || null,
          logo_size: logoSize,
          folder_id: selectedFolderId || null
        };
        
        const response = await qrcodesAPI.create(qrCodeData);
        const qrCodeId = response.data.data.id;
        setSavedQrCodeId(qrCodeId);

        // Generate a redirect-based QR and store it
        const redirectQrDataUrl = await generateQRCodeWithRedirect(qrCodeId);
        setQrCodeUrl(redirectQrDataUrl);

        await qrcodesAPI.update(qrCodeId, {
          qr_code_image: redirectQrDataUrl,
          qr_code_settings: JSON.stringify({
            size: qrSize,
            errorCorrection: qrErrorCorrection,
            colorDark: qrColorDark,
            colorLight: qrColorLight,
            margin: qrMargin
          }),
          logo_image: logoImage || null,
          logo_size: logoSize,
          folder_id: selectedFolderId || null
        });

        toast.success('Dynamic QR code saved successfully! Updating the URL later will keep the same printed QR.');
      }
    } catch (error) {
      console.error('Error saving QR code:', error);
      toast.error('Failed to save QR code: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const renderInputFields = () => {
    switch (qrType) {
      case 'URL':
        return (
          <InputGroup>
            <Label>Website URL *</Label>
            <Input
              type="url"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </InputGroup>
        );
      
      case 'TEXT':
        return (
          <InputGroup>
            <Label>Text Content *</Label>
            <TextArea
              placeholder="Enter any text..."
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </InputGroup>
        );
      
      case 'EMAIL':
        return (
          <>
            <InputGroup>
              <Label>Email Address *</Label>
              <Input
                type="email"
                placeholder="example@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Subject (Optional)</Label>
              <Input
                type="text"
                placeholder="Email subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Body (Optional)</Label>
              <TextArea
                placeholder="Email body text"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
              />
            </InputGroup>
          </>
        );
      
      case 'PHONE':
        return (
          <InputGroup>
            <Label>Phone Number *</Label>
            <Input
              type="tel"
              placeholder="+1234567890"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </InputGroup>
        );
      
      case 'SMS':
        return (
          <>
            <InputGroup>
              <Label>Phone Number *</Label>
              <Input
                type="tel"
                placeholder="+1234567890"
                value={smsPhone}
                onChange={(e) => setSmsPhone(e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Message (Optional)</Label>
              <TextArea
                placeholder="SMS message text"
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
              />
            </InputGroup>
          </>
        );
      
      case 'WIFI':
        return (
          <>
            <InputGroup>
              <Label>Network Name (SSID) *</Label>
              <Input
                type="text"
                placeholder="WiFi Network Name"
                value={wifiSSID}
                onChange={(e) => setWifiSSID(e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Password</Label>
              <Input
                type="password"
                placeholder="WiFi Password"
                value={wifiPassword}
                onChange={(e) => setWifiPassword(e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Security Type</Label>
              <Select
                value={wifiSecurity}
                onChange={(e) => setWifiSecurity(e.target.value)}
              >
                <option value="WPA">WPA/WPA2</option>
                <option value="WEP">WEP</option>
                <option value="None">None (Open Network)</option>
              </Select>
            </InputGroup>
            <InputGroup>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={wifiHidden}
                  onChange={(e) => setWifiHidden(e.target.checked)}
                />
                <span>Hidden Network</span>
              </label>
            </InputGroup>
          </>
        );
      
      case 'VCARD':
        return (
          <>
            <InputGroup>
              <Label>Full Name *</Label>
              <Input
                type="text"
                placeholder="John Doe"
                value={vcardName}
                onChange={(e) => setVcardName(e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Organization</Label>
              <Input
                type="text"
                placeholder="Company Name"
                value={vcardOrg}
                onChange={(e) => setVcardOrg(e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Phone</Label>
              <Input
                type="tel"
                placeholder="+1234567890"
                value={vcardPhone}
                onChange={(e) => setVcardPhone(e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="example@email.com"
                value={vcardEmail}
                onChange={(e) => setVcardEmail(e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Website</Label>
              <Input
                type="url"
                placeholder="https://example.com"
                value={vcardWebsite}
                onChange={(e) => setVcardWebsite(e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Address</Label>
              <Input
                type="text"
                placeholder="Street Address"
                value={vcardAddress}
                onChange={(e) => setVcardAddress(e.target.value)}
              />
            </InputGroup>
          </>
        );
      
      default:
        return null;
    }
  };

  // Clear QR code when type changes
  useEffect(() => {
    setQrCodeUrl('');
  }, [qrType]);

  // Fetch folders on component mount
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        setLoadingFolders(true);
        const response = await foldersAPI.getFolders();
        console.log('Folders response:', response.data);
        setFolders(response.data.data || []);
      } catch (error) {
        console.error('Error fetching folders:', error);
        console.error('Error details:', error.response?.data);
        // Don't show error toast, folders are optional
        setFolders([]);
      } finally {
        setLoadingFolders(false);
      }
    };
    fetchFolders();
  }, []);

  return (
    <Container>
      <PageHeader>
        <PageTitle>
          <FiSettings />
          Dynamic QR Code Generator
        </PageTitle>
        <PageDescription>
          Create QR codes for URLs, text, contact information, WiFi networks, and more
        </PageDescription>
      </PageHeader>

      <ContentGrid>
        <InputSection>
          <SectionTitle>QR Code Information</SectionTitle>
          <InputGroup>
            <Label>Title *</Label>
            <Input
              type="text"
              placeholder="Enter QR code title"
              value={qrTitle}
              onChange={(e) => setQrTitle(e.target.value)}
            />
          </InputGroup>
          <InputGroup>
            <Label>Folder (Optional)</Label>
            <Select
              value={selectedFolderId || ''}
              onChange={(e) => setSelectedFolderId(e.target.value || null)}
              disabled={loadingFolders}
              style={{ 
                border: '2px solid #e5e7eb',
                backgroundColor: selectedFolderId ? '#f0f9ff' : 'white'
              }}
            >
              <option value="">No Folder</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </Select>
            {loadingFolders && (
              <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                Loading folders...
              </small>
            )}
            {!loadingFolders && folders.length === 0 && (
              <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                No folders available. Create folders from the Dashboard to organize your QR codes.
              </small>
            )}
          </InputGroup>
          <InputGroup>
            <Label>Description (Optional)</Label>
            <TextArea
              placeholder="Enter description for this QR code"
              value={qrDescription}
              onChange={(e) => setQrDescription(e.target.value)}
              style={{ minHeight: '60px' }}
            />
          </InputGroup>

          <SectionTitle>Select Type</SectionTitle>
          <TypeSelector>
            {Object.entries(QR_TYPES).map(([type, config]) => {
              const Icon = config.icon;
              return (
                <TypeButton
                  key={type}
                  active={qrType === type}
                  onClick={() => {
                    setQrType(type);
                    setQrCodeUrl(''); // Clear previous QR code
                  }}
                >
                  <Icon />
                  {config.label}
                </TypeButton>
              );
            })}
          </TypeSelector>

          <SectionTitle>Enter Information</SectionTitle>
          {renderInputFields()}

          <SettingsSection>
            <SectionTitle>
              <FiImage />
              Logo Settings
            </SectionTitle>
            <InputGroup>
              <FileInput
                type="file"
                id="logo-upload"
                accept="image/*"
                onChange={handleLogoUpload}
              />
              <FileInputLabel htmlFor="logo-upload">
                <FiImage style={{ marginRight: '8px' }} />
                {logoImage ? 'Change Logo' : 'Upload Logo'}
              </FileInputLabel>
              {logoImage && (
                <LogoPreview>
                  <LogoPreviewImage src={logoImage} alt="Logo preview" />
                  <RemoveLogoButton onClick={removeLogo} title="Remove logo">
                    <FiX />
                  </RemoveLogoButton>
                </LogoPreview>
              )}
            </InputGroup>
            {logoImage && (
              <InputGroup>
                <Label>Logo Size (% of QR code)</Label>
                <Input
                  type="number"
                  min="10"
                  max="40"
                  value={logoSize}
                  onChange={(e) => setLogoSize(parseInt(e.target.value) || 20)}
                />
                <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Recommended: 15-25% for best scanability
                </small>
              </InputGroup>
            )}
          </SettingsSection>

          <SettingsSection>
            <SectionTitle>
              <FiSettings />
              QR Code Settings
            </SectionTitle>
            <SettingsGrid>
              <InputGroup>
                <Label>Size (px)</Label>
                <Input
                  type="number"
                  min="100"
                  max="1000"
                  value={qrSize}
                  onChange={(e) => setQrSize(parseInt(e.target.value) || 300)}
                />
              </InputGroup>
              <InputGroup>
                <Label>Error Correction</Label>
                <Select
                  value={qrErrorCorrection}
                  onChange={(e) => setQrErrorCorrection(e.target.value)}
                >
                  <option value="L">L (Low ~7%)</option>
                  <option value="M">M (Medium ~15%)</option>
                  <option value="Q">Q (Quartile ~25%)</option>
                  <option value="H">H (High ~30%)</option>
                </Select>
              </InputGroup>
              <InputGroup>
                <Label>Dark Color</Label>
                <Input
                  type="color"
                  value={qrColorDark}
                  onChange={(e) => setQrColorDark(e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <Label>Light Color</Label>
                <Input
                  type="color"
                  value={qrColorLight}
                  onChange={(e) => setQrColorLight(e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <Label>Margin</Label>
                <Input
                  type="number"
                  min="0"
                  max="10"
                  value={qrMargin}
                  onChange={(e) => setQrMargin(parseInt(e.target.value) || 4)}
                />
              </InputGroup>
            </SettingsGrid>
          </SettingsSection>

          <SettingsSection>
            <SectionTitle>
              <FiSettings />
              QR Code Style
            </SectionTitle>
            <SettingsGrid>
              <InputGroup>
                <Label>Background Color</Label>
                <Input
                  type="color"
                  value={qrBackgroundColor}
                  onChange={(e) => setQrBackgroundColor(e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <Label>Background Pattern</Label>
                <Select
                  value={qrBackgroundPattern}
                  onChange={(e) => setQrBackgroundPattern(e.target.value)}
                >
                  <option value="none">None</option>
                  <option value="gradient">Gradient</option>
                  <option value="dots">Dots</option>
                  <option value="lines">Lines</option>
                </Select>
              </InputGroup>
              <InputGroup>
                <Label>Border Radius (px)</Label>
                <Input
                  type="number"
                  min="0"
                  max="50"
                  value={qrBorderRadius}
                  onChange={(e) => setQrBorderRadius(parseInt(e.target.value) || 0)}
                />
              </InputGroup>
              <InputGroup>
                <Label>Border Width (px)</Label>
                <Input
                  type="number"
                  min="0"
                  max="20"
                  value={qrBorderWidth}
                  onChange={(e) => setQrBorderWidth(parseInt(e.target.value) || 0)}
                />
              </InputGroup>
              {qrBorderWidth > 0 && (
                <InputGroup>
                  <Label>Border Color</Label>
                  <Input
                    type="color"
                    value={qrBorderColor}
                    onChange={(e) => setQrBorderColor(e.target.value)}
                  />
                </InputGroup>
              )}
            </SettingsGrid>
          </SettingsSection>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <GenerateButton 
              onClick={generateQRCode} 
              disabled={isGenerating}
              style={{ flex: 1 }}
            >
              <FiRefreshCw style={{ animation: isGenerating ? 'spin 1s linear infinite' : 'none' }} />
              {isGenerating ? 'Generating...' : 'Generate QR Code'}
            </GenerateButton>
            <ActionButton
              onClick={saveQRCode}
              disabled={isSaving || !qrCodeUrl}
              primary
              style={{ flex: 1 }}
            >
              <FiSave style={{ animation: isSaving ? 'spin 1s linear infinite' : 'none' }} />
              {isSaving ? 'Saving...' : savedQrCodeId ? 'Update Content' : 'Save QR Code'}
            </ActionButton>
          </div>
        </InputSection>

        <PreviewSection>
          <SectionTitle>QR Code Preview</SectionTitle>
          <QRCodeContainer>
            {qrCodeUrl ? (
              <>
                <QRCodeImage src={qrCodeUrl} alt="Generated QR Code" />
                <ActionButtons>
                  <ActionButton onClick={downloadQRCode}>
                    <FiDownload />
                    Download
                  </ActionButton>
                  <ActionButton onClick={copyQRCode}>
                    <FiCopy />
                    Copy
                  </ActionButton>
                </ActionButtons>
              </>
            ) : (
              <QRCodePlaceholder>
                Your QR code will appear here
              </QRCodePlaceholder>
            )}
          </QRCodeContainer>
        </PreviewSection>
      </ContentGrid>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  );
}

export default QRCodeGenerator;

